import React, { lazy, Suspense, useState, useEffect } from 'react';
import { BrowserRouter as Router, Route, Switch } from 'react-router-dom';
import { getAuthToken, removeAuthToken } from 'localStorageService';
import { Toaster } from 'react-hot-toast';
import NiceModal from '@ebay/nice-modal-react';

import { PageTypes, API_URL } from 'constants/Constants';
import PrivateRoute from './PrivateRoute';

// LAYOUT
import PageContainer from './components/layout/PageContainer/PageContainer';
import AppHeader from './components/layout/AppHeader/AppHeader';
import PageLoadingComponent from './components/layout/PageLoadingComponent/PageLoadingComponent';
// PAGES
// Settings
const SettingsPage = lazy(() => import('./pages/Settings/SettingsPage/SettingsPage'));
const GlobalContentParsePatternPage = lazy(() => import('./pages/Settings/GlobalContentParsePatternPage/GlobalContentParsePatternPage'));
// Auth
const LoginPage = lazy(() => import('./pages/LoginPage/LoginPage'));
const SignUpPage = lazy(() => import('./pages/SignUpPage/SignUpPage'));
// Queries
const QueriesOverviewPage = lazy(() => import('./pages/Queries/QueriesOverviewPage/QueriesOverviewPage'));
const QueryPage = lazy(() => import('./pages/Queries/QueryPage/QueryPage'));
// MonitoredSources
const MonitoredSourcesOverviewPage = lazy(() =>
	import('./pages/MonitoredSources/MonitoredSourcesOverviewPage/MonitoredSourcesOverviewPage')
);
const MonitoredSourcePage = lazy(() => import('pages/MonitoredSources/MonitoredSourcePage/MonitoredSourcePage'));
// Profiles
const ProfilesOverviewPage = lazy(() => import('./pages/Profiles/ProfilesOverviewPage/ProfilesOverviewPage'));
const ProfilePage = lazy(() => import('./pages/Profiles/ProfilePage/ProfilePage'));
// Categories
const CategoriesOverviewPage = lazy(() => import('./pages/Categories/CategoryOverviewPage/CategoryOverviewPage'));
const CategoryPage = lazy(() => import('./pages/Categories/CategoryPage/CategoryPage'));
// sites
const SiteOverviewPage = lazy(() => import('pages/Sites/SiteOverviewPage/SiteOverviewPage'));
const SitePage = lazy(() => import('pages/Sites/SitePage/SitePage'));
// Posts
const PostsOverviewPage = lazy(() => import('./pages/Posts/PostsOverviewPage/PostsOverviewPage'));
const PostPage = lazy(() => import('./pages/Posts/PostPage/PostPage'));
// LOGS
const LogsPage = lazy(() => import('./pages/Logs/LogsPage/LogsPage'));

function App() {
	const hasValidToken = async function() {
		const tokenFromLocalStorage = getAuthToken();
		if (!tokenFromLocalStorage) {
			return false;
		}

		const settings = {
			method: 'GET',
			headers: {
				Accept: 'application/json',
				'Content-Type': 'application/json',
				authorization: tokenFromLocalStorage ? `Bearer ${tokenFromLocalStorage}` : ''
			}
		};

		const asyncIsValid = await fetch(`${API_URL}/api/test/auth`, settings)
			.then(res => {
				if (res.ok && res.status === 200) {
					return true;
				}
				removeAuthToken();
				window.location.href('/');
				return false;
			})
			.catch(e => {
				console.error('Could not authenticate token', e); // eslint-disable-line
				removeAuthToken();
				return false;
			});

		return asyncIsValid;
	};

	useEffect(() => {
		Promise.resolve(hasValidToken())
			.then(res => {
				setIsAuthenticated({ auth: res, pending: false });
			})
			.catch(e => {
				console.error('Token is not valid', e); // eslint-disable-line
				setIsAuthenticated({ auth: false, pending: false });
			});
	}, []);

	const [authenticated, setIsAuthenticated] = useState({ auth: false, pending: true });

	const PRIVATE_ROUTES = [
		{ path: '/', component: MonitoredSourcesOverviewPage, authenticated, exact: true },
		{ path: PageTypes.POSTS.path, component: PostsOverviewPage, authenticated, exact: true },
		{ path: PageTypes.POST_PAGE.path, component: PostPage, authenticated, exact: true },
		{ path: PageTypes.PROFILES.path, component: ProfilesOverviewPage, authenticated, exact: true },
		{ path: PageTypes.PROFILE_PAGE.path, component: ProfilePage, authenticated, exact: true },
		{ path: PageTypes.QUERIES.path, component: QueriesOverviewPage, authenticated, exact: true },
		{ path: PageTypes.MONITORED_SOURCES.path, component: MonitoredSourcesOverviewPage, authenticated, exact: true },
		{ path: PageTypes.CATEGORIES.path, component: CategoriesOverviewPage, authenticated, exact: true },
		{ path: PageTypes.CATEGORY_PAGE.path, component: CategoryPage, authenticated, exact: true },
		{ path: PageTypes.MONITORED_SOURCE_PAGE.path, component: MonitoredSourcePage, authenticated, exact: true },
		{ path: PageTypes.QUERY_PAGE.path, component: QueryPage, authenticated, exact: true },
		{ path: PageTypes.SITES.path, component: SiteOverviewPage, authenticated, exact: true },
		{ path: PageTypes.SITE_PAGE.path, component: SitePage, authenticated, exact: true },
		{ path: PageTypes.SETTINGS_PAGE.path, component: SettingsPage, authenticated, exact: true },
		{ path: PageTypes.LOGS_PAGE.path, component: LogsPage, authenticated, exact: true },
		{ path: PageTypes.GLOBAL_CONTENT_PARSE_PATTERN_PAGE.path, component: GlobalContentParsePatternPage, authenticated, exact: true }
	];

	return (
		<Router>
			<Toaster
				position={'top-right'}
				reverseOrder={false}
				toastOptions={{
					className: '',
					duration: 3000,
					style: {
						fontFamily: '"Assistant" open-sans'
					},
					success: {
						iconTheme: {
							style: {
								background: '#058736'
							}
						}
					},
					error: {
						duration: 4000,
						iconTheme: {
							style: {
								background: '#d0341b'
							}
						}
					}
				}}
			/>
			<NiceModal.Provider>
				<AppHeader
					authenticated={authenticated.auth}
					onIsAuthenticatedCallback={() => {
						setIsAuthenticated({ auth: false, pending: false });
					}}
				/>
				<PageContainer>
					<Suspense fallback={<PageLoadingComponent />}>
						<Switch>
							<Route
								path={PageTypes.LOGIN_PAGE.path}
								component={() => (
									<LoginPage
										onIsAuthenticatedCallback={() => {
											setIsAuthenticated({ auth: true, pending: false });
										}}
										authenticated={authenticated.auth}
									/>
								)}
							/>
							<Route path={PageTypes.SIGN_UP_PAGE.path} component={SignUpPage} />
							{/* Private routes */}
							{PRIVATE_ROUTES.map(route => (
								<PrivateRoute
									key={route.path}
									onIsAuthenticatedCallback={() => {
										setIsAuthenticated({ auth: true, pending: false });
									}}
									{...route}
								/>
							))}
						</Switch>
					</Suspense>
				</PageContainer>
			</NiceModal.Provider>
		</Router>
	);
}

export default App;
